import { ReactNode, useEffect, useRef } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled, { createGlobalStyle } from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { HeadlineS } from '@sorare/core/src/atoms/typography';
import { Section } from '@sorare/core/src/components/layout/MainAsideLayout';
import { Warning } from '@sorare/core/src/contexts/intl/Warning';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { usePreserveScrollWhenLoadingPreviousElements } from '@sorare/core/src/hooks/usePreserveScrollWhenLoadingPreviousElements';

import { Game, Props as GameProps } from 'components/seo/Games/Game';

const messages = defineMessages({
  nameUpcomingFixtures: {
    id: 'Seo.Matches.nameUpcomingFixtures',
    defaultMessage: '{displayName} upcoming fixtures',
  },
  nameUpcomingGames: {
    id: 'Seo.Matches.nameUpcomingGames',
    defaultMessage: '{displayName} upcoming games',
  },
  noFixtures: {
    id: 'seo.Club.Standings.noFixtures',
    defaultMessage: 'No fixtures for {displayName}',
  },
  noFutureFixtures: {
    id: 'seo.Club.Standings.noFutureFixtures',
    defaultMessage: 'No future fixtures for {displayName}',
  },
  noFutureGames: {
    id: 'seo.Club.Standings.noFutureGames',
    defaultMessage: 'No future games for {displayName}',
  },
});

const Root = styled(FullWidthContainer)`
  --game-height-approx: 120px;
  --infinite-scroll-height: 60px;
  padding-bottom: var(--double-unit);
`;

const StyledHeadlineS = styled(HeadlineS)`
  scroll-margin-top: calc(
    var(--current-stack-height) + var(--game-height-approx)
  );
`;
const Games = styled.div`
  --game-background: transparent;

  background: var(--c-nd-50);
  border-radius: var(--double-unit);
  &.loaderOnTop {
    border-top-right-radius: 0;
    border-top-left-radius: 0;
  }

  & > *:not(:last-child) {
    border-bottom: 1px solid var(--c-black);
  }
`;
const GameWrapper = styled.div`
  scroll-margin-top: calc(
    var(--current-stack-height) + var(--infinite-scroll-height)
  );
`;
const InfiniteScrollContainer = styled(Horizontal).attrs({
  gap: 0,
  center: true,
})`
  height: var(--infinite-scroll-height);
`;
const DisableNativeRefresh = createGlobalStyle`
  html, body {
    overscroll-behavior: none;
  }
`;

type Props = {
  displayName: string;
  loading: boolean;
  pastGames: GameProps['game'][];
  infiniteScrollLoaderPast: ReactNode;
  futureGames: GameProps['game'][];
  infiniteScrollLoaderFuture: ReactNode;
};

export const Matches = ({
  displayName,
  loading,
  pastGames,
  infiniteScrollLoaderPast,
  futureGames,
  infiniteScrollLoaderFuture,
}: Props) => {
  const { sport } = useSportContext();
  const pastGamesContainerRef = useRef<HTMLDivElement>(null);

  const noGames = !loading && !pastGames.length && !futureGames.length;
  const loaded = !!pastGames.length && !!futureGames.length;
  const ref = useRef<HTMLDivElement>(null);
  const hasScrolled = useRef(false);

  useEffect(() => {
    if (noGames) {
      // the `EnsureTopIsVisible` is disabled on those pages, so we need to scroll manually
      // top the top when there are no games
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (ref.current && loaded) {
      // scroll to the top of the upcoming games section
      ref.current.scrollIntoView({ block: 'start', behavior: 'instant' });
      hasScrolled.current = true;
    }
  }, [loaded, noGames]);

  usePreserveScrollWhenLoadingPreviousElements({
    items: pastGames,
    getNode: game =>
      pastGamesContainerRef.current?.querySelector(
        `[data-game-id="${game.id}"]`
      ) ?? null,
  });

  if (noGames) {
    return (
      <Section topPadding>
        <FullWidthContainer>
          <Warning
            title={messages.noFixtures}
            variant="yellow"
            titleValues={{ displayName }}
          />
        </FullWidthContainer>
      </Section>
    );
  }

  if (loading) {
    return <LoadingIndicator fullHeight />;
  }

  return (
    <Root>
      <DisableNativeRefresh />
      <Vertical gap={3}>
        <Games ref={pastGamesContainerRef} className="loaderOnTop">
          {hasScrolled.current && (
            <InfiniteScrollContainer>
              {infiniteScrollLoaderPast}
            </InfiniteScrollContainer>
          )}
          {pastGames.map(game => (
            <GameWrapper key={game.id} data-game-id={game.id}>
              <Game game={game} />
            </GameWrapper>
          ))}
        </Games>
        <Vertical gap={1.5}>
          <StyledHeadlineS as="h1" ref={ref}>
            <FormattedMessage
              {...(sport === Sport.FOOTBALL
                ? messages.nameUpcomingFixtures
                : messages.nameUpcomingGames)}
              values={{ displayName }}
            />
          </StyledHeadlineS>
          <Games>
            {!loading && !futureGames.length && (
              <Warning
                variant="yellow"
                title={
                  sport === Sport.FOOTBALL
                    ? messages.noFutureFixtures
                    : messages.noFutureGames
                }
                titleValues={{ displayName }}
              />
            )}
            {futureGames.map(game => (
              <GameWrapper key={game.id} data-game-id={game.id}>
                <Game game={game} />
              </GameWrapper>
            ))}
            {infiniteScrollLoaderFuture}
          </Games>
        </Vertical>
      </Vertical>
    </Root>
  );
};
